import type { NumericPoint } from "@incytestudios/ddv";
import { isFinitePoint, isNaturalPoint } from "../_common";
import { normalizeRequiredTDHPoints } from "../_common/requiredTdhOutlierFilter";
import {
  getSimplifiedProducedTdh,
  type SinglePumpChartContext,
  type SinglePumpChartRequiredTDHDataSet
} from "..";
import { convert } from "@/libs/units";
import type { XrefPumps } from "@/composables/forms/formTypes";

// If it is an HPS scenario, the discharge flow rate and req TDH are
// static and will not draw on the chart since they are all the same point
export const getRequiredTDHPoints = (
  context: SinglePumpChartContext,
  densityCorrectionRatio: number
) => {
  const {
    hypotheticals: { hypotheticalCalcs },
    maxFlowRateBBLD,
    pumpIntakeFlowRateBBLD,
    pumpDischargeFlowRateBBLD,
    graphMax,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    ascendingOrderPumps,
    isScenarioEspShore,
    dischargeFluidDensityLBMFT3,
    frequencyRatio
  } = context;

  let requiredTDHPoints: NumericPoint[] = [];
  if (isScenarioEspShore) {
    // Filter required TDH points to only those within 15% of the graph max around the origin
    // Used to prevent extreme points from skewing the chart view
    const THRESHOLD = 0.15;
    const upperThreshold = Math.min(
      maxFlowRateBBLD,
      pumpIntakeFlowRateBBLD + graphMax * THRESHOLD
    );
    requiredTDHPoints = hypotheticalCalcs
      .map<NumericPoint>(
        ({ requiredTotalDynamicHeadFt, pumpDischargeFlowRateBBLD }) => {
          return {
            x: pumpDischargeFlowRateBBLD,
            y: requiredTotalDynamicHeadFt
          };
        }
      )
      .filter(pt => pt.x < upperThreshold);

    requiredTDHPoints = normalizeRequiredTDHPoints(requiredTDHPoints);
  } else {
    // Retrieve produced tdh for intake and discharge points to build a range
    const requiredTotalDynamicHeadFt =
      hypotheticalCalcs[0].requiredTotalDynamicHeadFt;
    const requiredPressurePSIA =
      hypotheticalCalcs[0].requiredDifferentialPressurePSIA;

    // This was originally written for tdh in ft. But now we're charting HPS scenarios
    // with tdh in pressure units
    const approxFlowRate = solveFlowRateForReqTDH(
      pumpDischargeFlowRateBBLD,
      pumpIntakeFlowRateBBLD,
      requiredTotalDynamicHeadFt,
      frequencyRatio,
      ascendingOrderPumps,
      gasHeadRerateRatio,
      pumpWearHeadCorrectionRatio,
      dischargeFluidDensityLBMFT3,
      densityCorrectionRatio
    ).flowRate;
    requiredTDHPoints.push({
      x: approxFlowRate,
      y: requiredPressurePSIA
    });
  }
  return requiredTDHPoints;
};

export const getDataSetRequiredTDH = (
  context: SinglePumpChartContext,
  tdhDensityCorrectionRatio: number = 1
): SinglePumpChartRequiredTDHDataSet => {
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    isScenarioEspShore
  } = context;
  // ESP and HPS behave differently so it's ok to only pass in what we
  // need here
  const data = getRequiredTDHPoints(context, tdhDensityCorrectionRatio);
  const convertedRequiredTDHPoints = data
    .map<NumericPoint>(pt => {
      return {
        x: convert(pt.x, baseXUnit, targetXUnit),
        y: convert(pt.y, baseYUnit, targetYUnit)
      };
    })
    .filter(isFinitePoint)
    .filter(isNaturalPoint);

  const hpsRequiredTDHPoint = !isScenarioEspShore
    ? convertedRequiredTDHPoints[0]
    : undefined;

  const yAxisLabel = isScenarioEspShore
    ? `Head Produced`
    : `Differential Pressure`;

  const title = isScenarioEspShore ? "Required TDH" : "Required Pdisch";
  return {
    title,
    xAxisLabel: `In Situ Flow Rate (${targetXUnitLabel})`,
    yAxisLabel: `${yAxisLabel} (${targetYUnitLabel})`,
    points: convertedRequiredTDHPoints,
    originalPoints: data,
    hpsRequiredTDHPoint
  };
};

// static/Required flow algo.xlsx
export const solveFlowRateForReqTDH = (
  pumpDischargeFlowRateBBLD: number,
  pumpIntakeFlowRateBBLD: number,
  requiredTotalDynamicHeadFt: number,
  frequencyRatio: number,
  ascendingOrderPumps: XrefPumps,
  gasHeadRerateRatio: number,
  pumpWearHeadCorrectionRatio: number,
  dischargeFluidDensityLBMFT3: number,
  densityCorrectionRatio: number = 1
) => {
  // Lower TDH will have a higher flow rate so that's why
  // we use the flow rate from the high record as our minimum
  let MIN_STARTING_FLOW_RATE = pumpDischargeFlowRateBBLD;
  let MAX_FLOW_RATE = pumpIntakeFlowRateBBLD;
  let minFlowRate = pumpDischargeFlowRateBBLD;
  let maxFlowRate = pumpIntakeFlowRateBBLD;

  const isDischargeFlowRateLessThanIntake =
    pumpDischargeFlowRateBBLD < pumpIntakeFlowRateBBLD;
  // If for some reason the intake rate is less than the discharge rate, swap them
  if (!isDischargeFlowRateLessThanIntake) {
    MIN_STARTING_FLOW_RATE = pumpIntakeFlowRateBBLD;
    MAX_FLOW_RATE = pumpDischargeFlowRateBBLD;
    minFlowRate = pumpIntakeFlowRateBBLD;
    maxFlowRate = pumpDischargeFlowRateBBLD;
  }

  let searchStartFlowRate = MIN_STARTING_FLOW_RATE;

  let bestResult = { error: 0, flowRate: searchStartFlowRate, tdh: 0 };

  const stages = [
    { maxIterations: 100 }, // Coarse search
    { maxIterations: 200 }, // Fine search
    { maxIterations: 300 } // Ultra-fine search for precision
  ];

  for (const [stageIndex, stage] of stages.entries()) {
    let minError = Infinity;
    let lastError = Infinity;
    let flowRate = searchStartFlowRate;
    const step = (maxFlowRate - minFlowRate) / stage.maxIterations;
    for (let i = 0; i < stage.maxIterations && flowRate <= maxFlowRate; i++) {
      // THIS CORRECTIN RATIO NEEDS TO BE TDH, NOT PRESSURE
      const tdh: number =
        getSimplifiedProducedTdh(
          flowRate,
          frequencyRatio,
          ascendingOrderPumps,
          gasHeadRerateRatio,
          pumpWearHeadCorrectionRatio,
          dischargeFluidDensityLBMFT3
        ).tdh * densityCorrectionRatio;
      const currentError = Math.abs(tdh - requiredTotalDynamicHeadFt);
      if (currentError < minError) {
        minError = currentError;
        bestResult = { error: currentError, flowRate, tdh };
      }
      // For stages after the first, if error grows, we've passed the
      // minimum for this granularity. Stop this stage and prepare for
      // the next, finer stage.
      if (stageIndex > 0 && i > 0 && currentError > lastError) {
        break;
      }

      lastError = currentError;
      flowRate += step;
    }

    // Exit early if result is within acceptable tolerance
    if (bestResult.error <= 0.01) {
      break;
    }
    // Prepare for the next stage by starting the search just before
    // the best-found flowrate and from the current stage.
    searchStartFlowRate = bestResult.flowRate - step;
    if (searchStartFlowRate < MIN_STARTING_FLOW_RATE)
      searchStartFlowRate = MIN_STARTING_FLOW_RATE;
    minFlowRate = searchStartFlowRate;
    maxFlowRate = minFlowRate + step * stage.maxIterations;
    if (maxFlowRate > MAX_FLOW_RATE) maxFlowRate = MAX_FLOW_RATE;
  }

  const returnResult = {
    flowRate: bestResult.flowRate,
    tdh: bestResult.tdh,
    error: bestResult.error
  };
  return returnResult;
};
